import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminStore } from '../../stores/adminStore';
import { useTranslation } from '../../hooks/useTranslation';
import { useMoneyDisplay } from '../../utils/money';
import { toLocalizedFormat, UNIVERSAL_DATE_FORMAT } from '@dutchinvoice/shared-utils';
import { useInvoiceStore } from '../../stores/invoiceStore';
import { calculateInvoiceTotals } from '../../utils/invoiceCalculations';
import { format } from 'date-fns';
import { PaymentStatusBadge } from '../../components/PaymentStatus';
import { LocalizedDate } from '../../components/shared/LocalizedDate';
import { LocalizedLink } from '../../components/LocalizedLink';
import { adminApi } from '../../lib/api';
import { Mail } from 'lucide-react';

export const UserInvoices: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { uid } = useParams<{ uid: string }>();
  const language = useInvoiceStore(state => state.settings.language);
  const { formatCurrency } = useMoneyDisplay();
  const {
    selectedUser,
    userInvoices,
    userInvoicesLoading,
    userInvoicesError,
    hasMore,
    fetchUserDetails,
    fetchUserInvoices,
    loadMoreUserInvoices
  } = useAdminStore();
  
  // State for email preview modal
  const [emailPreview, setEmailPreview] = useState<{ html: string; invoiceNumber: string } | null>(null);
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);

  useEffect(() => {
    if (!uid) {
      navigate('/admin');
      return;
    }
    
    fetchUserDetails(uid);
    fetchUserInvoices(uid);
  }, [uid, navigate, fetchUserDetails, fetchUserInvoices]);

  // Function to fetch and show email preview
  const handleEmailPreview = async (e: React.MouseEvent, invoiceId: string) => {
    e.stopPropagation(); // Prevent row click navigation
    
    if (!uid) return;
    
    setIsLoadingEmail(true);
    try {
      const result = await adminApi.getInvoiceEmailPreview(uid, invoiceId);
      
      // Get just the HTML part from the API response
      setEmailPreview({
        html: result.html,
        invoiceNumber: result.invoiceNumber
      });
    } catch (error) {
      console.error('Failed to get email preview:', error);
    } finally {
      setIsLoadingEmail(false);
    }
  };

  // Memoize invoice totals to avoid recalculating in the render function
  const invoiceTotals = React.useMemo(() => {
    return userInvoices.reduce((acc, invoice) => {
      if (invoice && invoice.id) {
        acc[invoice.id] = calculateInvoiceTotals(invoice.items, invoice.discount).total;
      }
      return acc;
    }, {} as Record<string, string>);
  }, [userInvoices]);

  if (!selectedUser) {
    console.log('[UserInvoices] selectedUser is null, showing loading state');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50/50 backdrop-blur-xl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Ensure selectedUser has settings to prevent errors
  if (!selectedUser.settings) {
    console.log('[UserInvoices] selectedUser.settings is missing, adding empty object');
    selectedUser.settings = {
      language: 'en',
      country: 'NL',
      invoiceStartNumber: 1,
      latestInvoiceNumber: 0,
      invoiceNumberFormat: {
        prefix: 'INV',
        includeYear: true,
        yearFormat: 'full',
        separator: '-',
        numberPadding: 4
      }
    };
  }

  console.log('[UserInvoices] Rendering component with data');

  // Convert timestamp to date string in universal format
  const createdAtDate = format(new Date(selectedUser.createdAt), UNIVERSAL_DATE_FORMAT);

  return (
    <div className="min-h-screen bg-gray-50/50 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <LocalizedLink
              to="/admin"
              className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('admin.backToUsers')}
            </LocalizedLink>
            <h1 className="text-2xl font-semibold text-gray-900">
              {t('admin.userInvoices.title', { email: selectedUser.email })}
            </h1>
          </div>
          <div className="flex space-x-3">
            <LocalizedLink
              to={`/admin/user/${uid}/companies`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('admin.users.viewCompanies')}
            </LocalizedLink>
            <LocalizedLink
              to={`/admin/user/${uid}/settings`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('admin.users.viewSettings')}
            </LocalizedLink>
          </div>
        </div>

        {/* User Info Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">{t('admin.users.email')}</div>
              <div className="text-gray-900">{selectedUser.email}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">{t('admin.users.company')}</div>
              <div className="text-gray-900">{selectedUser.defaultCompany || '-'}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">{t('admin.users.joined')}</div>
              <div className="text-gray-900">{toLocalizedFormat(createdAtDate, language)}</div>
            </div>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-medium text-gray-900">
              {t('admin.userInvoices.invoices')} ({userInvoices.length})
            </h2>
          </div>
          
          {userInvoicesLoading && userInvoices.length === 0 ? (
            <div className="p-6 text-center text-gray-500">{t('common.loading')}</div>
          ) : userInvoicesError ? (
            <div className="p-6 text-center text-red-500">{userInvoicesError}</div>
          ) : userInvoices.length === 0 ? (
            <div className="p-6 text-center text-gray-500">{t('admin.userInvoices.noInvoices')}</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead>
                    <tr className="bg-gray-50/50">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoice.number')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoice.to')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoice.date')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoice.total')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('invoice.status')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status Page
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {userInvoices.map((invoice) => (
                      <tr 
                        key={invoice.id} 
                        className="hover:bg-gray-50/50 transition-colors duration-150 cursor-pointer" 
                        onClick={() => navigate(`/admin/user/${uid}/invoice/${invoice.id}`)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.recipient.company}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <LocalizedDate date={invoice.date} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.id && invoiceTotals[invoice.id] ? formatCurrency(invoiceTotals[invoice.id]) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <PaymentStatusBadge status={invoice.paymentStatus} asChild />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <a 
                            href={`/invoice-status/${invoice.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            View
                          </a>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button
                            onClick={(e) => invoice.id && handleEmailPreview(e, invoice.id)}
                            className="text-primary-600 hover:text-primary-800 transition-colors"
                            title={t('admin.userInvoices.previewEmail')}
                          >
                            <Mail size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {hasMore && (
                <div className="px-6 py-4 border-t border-gray-100">
                  <button
                    onClick={() => uid && loadMoreUserInvoices(uid)}
                    disabled={userInvoicesLoading}
                    className="w-full btn-subtle"
                  >
                    {userInvoicesLoading ? t('common.loading') : t('admin.userInvoices.loadMore')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Email Preview Modal */}
      {emailPreview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                {t('admin.userInvoices.emailPreview', { invoiceNumber: emailPreview.invoiceNumber })}
              </h3>
              <button 
                onClick={() => setEmailPreview(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="border-b border-gray-200 bg-white px-4 py-2 flex items-center justify-between">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs text-gray-500">Email Preview</div>
                </div>
                <iframe
                  key={emailPreview.html}
                  srcDoc={`<!DOCTYPE html>
                    <html>
                      <head>
                        <meta charset="utf-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1">
                        <style>
                          body {
                            margin: 0;
                            font-family: system-ui, -apple-system, sans-serif;
                            background-color: #f8fafc;
                            padding: 20px;
                          }
                          img { max-width: 100%; height: auto; }
                          table { max-width: 100%; }

                          /* Hide Outlook conditionals */
                          [if], [endif], [if mso], [if !mso], [if gte mso], [if lte mso] {
                            display: none !important;
                          }
                        </style>
                      </head>
                      <body>${emailPreview.html}</body>
                    </html>`}
                  className="w-full h-[600px] border-0 bg-white"
                  title="Email Preview"
                  sandbox="allow-same-origin allow-scripts"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setEmailPreview(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Loading Overlay */}
      {isLoadingEmail && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-center">{t('admin.userInvoices.loadingEmail')}</p>
          </div>
        </div>
      )}
    </div>
  );
}; 